/**
 * useOrderBell — Web Audio API bell for the restaurant dashboard.
 *
 * Strategy (per product spec, 2026-06-08):
 *   1. Fire one short bell when a NEW pending order arrives (id not seen
 *      in the previous orders[] snapshot).
 *   2. While ANY order is still in `pending_restaurant_confirmation`
 *      (or legacy `pending`), repeat the bell every 20 seconds. The
 *      repetition stops the instant no pending order remains.
 *   3. Browser autoplay restrictions (Chrome / Safari) block AudioContext
 *      until the user gestures on the page once. We expose `enabled` +
 *      `enable()` so a banner can prompt "Enable Sound" and unlock the
 *      context with the user's click.
 *   4. Pure Web Audio — no MP3 files, no dependency. The bell is two
 *      stacked sine oscillators (G5 + C6) with a quick 220 ms decay
 *      envelope. Sounds clean across speakers / phones.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const REPEAT_MS = 20000;
const PENDING_STATUSES = new Set(['pending_restaurant_confirmation', 'pending']);

export default function useOrderBell(orders) {
  // Audio context is lazy + persists across renders. Created on the
  // user's first "Enable Sound" click so we never run into autoplay
  // policy errors at mount time.
  const ctxRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const intervalRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  // Has the browser indicated it MIGHT block playback? We optimistically
  // start as `needsPrompt=true` until the user clicks Enable Sound.
  const [needsPrompt, setNeedsPrompt] = useState(true);

  const ensureContext = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      ctxRef.current = new Ctx();
      return ctxRef.current;
    } catch {
      return null;
    }
  }, []);

  const playBell = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx || ctx.state === 'closed') return;
    // Resume just in case (some browsers suspend the context on tab
    // backgrounding even after the user has unlocked it once).
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});

    const now = ctx.currentTime;
    // Two-tone bell: G5 (784 Hz) + C6 (1046.5 Hz). Short attack + decay.
    const notes = [{ f: 784, gain: 0.18 }, { f: 1046.5, gain: 0.12 }];
    notes.forEach(({ f, gain }) => {
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      env.gain.setValueAtTime(0.0001, now);
      env.gain.exponentialRampToValueAtTime(gain, now + 0.01);
      env.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);
      osc.connect(env).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.45);
    });
  }, []);

  const enable = useCallback(() => {
    // ALWAYS flip the UI state first so a flaky AudioContext (headless
    // Chromium, locked-down corp browsers, screen-reader contexts) can
    // never trap the operator behind the Enable Sound banner. Audio
    // errors here are non-fatal — the worst-case is a silent bell.
    setEnabled(true);
    setNeedsPrompt(false);
    try {
      const ctx = ensureContext();
      if (!ctx) return;
      ctx.resume().catch(() => {});
      playBell();
    } catch {
      /* silent — see above */
    }
  }, [ensureContext, playBell]);

  // Watch for NEW pending orders to ring on first detection.
  useEffect(() => {
    if (!Array.isArray(orders)) return;
    const pending = orders.filter((o) => PENDING_STATUSES.has(o.status));
    const pendingIds = pending.map((o) => o.id);

    // Detect newly-arrived ids (not seen on previous tick).
    const newOnes = pendingIds.filter((id) => !seenIdsRef.current.has(id));
    if (newOnes.length > 0 && enabled) {
      playBell();
    }

    // Refresh the seen-set: only keep ids that are STILL pending so
    // that an order which gets accepted + later cycles back into
    // pending (rare) would re-ring. Old non-pending ids drop out.
    seenIdsRef.current = new Set(pendingIds);

    // ── Repeat loop ── ring once every REPEAT_MS as long as there's
    // at least one pending order. We tear down + rebuild the interval
    // every render so it always reads the latest `enabled`/`pending`.
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (enabled && pending.length > 0) {
      intervalRef.current = setInterval(() => {
        // Re-check pendingness at fire time (orders state may have
        // changed via accept/reject — don't rely on stale closure).
        playBell();
      }, REPEAT_MS);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [orders, enabled, playBell]);

  return { enabled, needsPrompt, enable };
}
